import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {
  BenchlingExpressionFunction,
  BenchlingExpressionType,
} from 'benchling-expressions/BenchlingExpressionTypes';
import {BenchlingFunctionSignature} from 'benchling-expressions/BenchlingFunctionSignatures';
import hoverAndActiveStyles from 'styles/hoverAndActiveStyles';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {immutableRemoveAt, immutableReplace} from 'util/ImmutableListUtil';
import {DeepPartial} from 'util/Types';

export interface ConditionOptionsButtonProps {
  idx: number;
  expression: DeepPartial<BenchlingExpressionFunction> | null;
  setExpression: (newExpression: DeepPartial<BenchlingExpressionFunction>) => void;
  isExpressionBoolean: boolean;
  defaultOperator: BenchlingFunctionSignature;
  recursiveDepth: number;
  didInitialFlowchartHaveChainedRouters: boolean;
  doesCurrentFlowchartHaveChainedRouters: boolean;
  dataTestAttrs?: DataTestAttrs;
}

export default function ConditionOptionsButton(props: ConditionOptionsButtonProps): JSX.Element | null {
  const {deleteDisabled, groupDisabled, deleteText, groupText} = useMemo(() => {
    assert(props.expression != null, 'Expected expression to be defined');
    const deleteDisabled = (props.expression?.parameters?.length ?? 0) <= 1;
    const deleteText = deleteDisabled ? 'Routers require at least one condition.' : undefined;
    const groupDisabled = props.recursiveDepth > 0 || props.isExpressionBoolean;
    const groupText =
      props.didInitialFlowchartHaveChainedRouters && props.doesCurrentFlowchartHaveChainedRouters
        ? 'To use condition groups, remove all chained routers in this flowchart.'
        : undefined;

    return {
      deleteDisabled,
      deleteText,
      groupDisabled,
      groupText,
    };
  }, [props]);

  function deleteExpression(): void {
    assert(props.expression != null, 'Expected expression to be defined');
    assert(
      props.expression.parameters != null && props.expression.parameters.length > 1,
      'Expected multiple router expression parameters'
    );

    const newParams = immutableRemoveAt(props.expression.parameters, props.idx);

    props.setExpression({
      ...props.expression,
      name: newParams.length === 1 ? props.defaultOperator.functionName : props.expression.name,
      parameters: newParams,
    });
  }

  function groupExpression(): void {
    assert(props.expression != null, 'Expected expression to be defined');
    assert(props.expression.parameters != null, 'Expected expression parameters to be defined');

    const newParams = immutableReplace(props.expression.parameters, props.idx, {
      name: props.defaultOperator.parameters[0].spreadParameterName,
      expression: {
        type: BenchlingExpressionType.FUNCTION,
        name: props.defaultOperator.functionName,
        parameters: [props.expression.parameters[props.idx]],
      },
    });
    props.setExpression({...props.expression, parameters: newParams});
  }

  return (
    <div {...getTestProps(props.dataTestAttrs)}>
      <Dropdown
        menu={
          <Menu height={Menu.HEIGHTS.SMALL} width={Menu.WIDTHS.SMALL}>
            <Option
              isDisabled={deleteDisabled}
              tooltipTitle={deleteText}
              onSelect={() => {
                deleteExpression();
              }}
            >
              Delete
            </Option>

            {!groupDisabled ? (
              <Option
                isDisabled={
                  props.didInitialFlowchartHaveChainedRouters && props.doesCurrentFlowchartHaveChainedRouters
                }
                tooltipTitle={groupText}
                onSelect={() => {
                  groupExpression();
                }}
              >
                Turn into group
              </Option>
            ) : null}
          </Menu>
        }
      >
        <Button
          className={css(styles.conditionOptionDropdown)}
          buttonStyle={ButtonStyle.TRANSPARENT}
          size={ButtonSize.MINI}
          icon="overflow"
        />
      </Dropdown>
    </div>
  );
}

const styles = StyleSheet.create({
  conditionOptionDropdown: {
    margin: '6px 8px 6px 8px',
    ...hoverAndActiveStyles({
      backgroundColor: GRAY,
    }),
  },
});
